import React from 'react';
import { imageUrl } from '../lib/images';
import SEO from '../components/SEO';
import SparklePattern from '../components/SparklePattern';

export function About() {
  return (
    <>
      <SEO
        title="Om mig | MA Hvidevareservice"
        description="Lær Mikki og MA Hvidevareservice at kende. Over 10 års erfaring med reparation af hårde hvidevarer på alle store mærker."
        ogImage="https://assets.auxodev.dk/assets/fa892c1f-aad0-492e-acb0-b4e96a2c8e1c/imgi-17-730373637-122104141388438737-209488069103445930-n.jpeg"
      />

      {/* Header section */}
      <section className="relative bg-white border-b border-slate-200/80 py-12 sm:py-16 overflow-hidden isolate">
        <SparklePattern variant="blue" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#53b7ff] tracking-tight">
              Om mig
            </h1>
          </div>
        </div>
      </section>

      {/* Main presentation grid */}
      <section className="relative py-14 sm:py-20 bg-[#38b6ff] border-b border-slate-200/80 overflow-hidden isolate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
            {/* Left Image column */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-slate-200 flex-1 min-h-[280px]">
                <img
                  src={imageUrl('/_assets/imgi-17-730373637-122104141388438737-209488069103445930-n.jpeg', 'width=900,quality=85,format=auto')}
                  alt="MA Hvidevareservice firmabil og servicevogn"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Quick stats box */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4 shrink-0">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-2xl font-extrabold text-slate-900">10+</div>
                    <div className="text-xs text-slate-500 font-medium">Års brancheerfaring</div>
                  </div>
                  <div>
                    <div className="text-2xl font-extrabold text-slate-900">1–2 år</div>
                    <div className="text-xs text-slate-500 font-medium">Garanti på arbejde & dele</div>
                  </div>
                  <div>
                    <div className="text-2xl font-extrabold text-slate-900">1 dag</div>
                    <div className="text-xs text-slate-500 font-medium">Typisk hurtigt hjemmebesøg</div>
                  </div>
                  <div>
                    <div className="text-2xl font-extrabold text-slate-900">995 kr.</div>
                    <div className="text-xs text-slate-500 font-medium">Fejlfinding inkl. kørsel</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Story column with verbatim text from scrape */}
            <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl p-6 sm:p-8 md:p-10 shadow-sm space-y-6">
              <div className="space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                <svg
                  className="w-10 h-10 sm:w-12 sm:h-12 text-[#38b6ff] mb-5 shrink-0"
                  viewBox="0 0 100 100"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="7"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M 50 5 C 52 28, 72 48, 95 50 C 72 52, 52 72, 50 95 C 48 72, 28 52, 5 50 C 28 48, 48 28, 50 5 Z" />
                </svg>
                <p className="text-base sm:text-lg font-medium text-slate-900 leading-snug">
                  Med over 10 års erfaring i branchen har jeg repareret alt fra vaskemaskiner og tørretumblere til køleskabe, ovne og opvaskemaskiner – på alle de store mærker.
                </p>

                <p>
                  Jeg startede min karriere som tekniker hos Danmarks største hvidevarevirksomhed, hvor jeg fik en grundig og bred uddannelse direkte fra producenterne. Den viden tager jeg med mig i dag, når jeg kommer ud til jer.
                </p>

                <p>
                  Jeg valgte at blive selvstændig. Hvorfor? Fordi jeg kan gøre det bedre og billigere end de store firmaer. Ingen unødvendige gebyrer, ingen kedelige tillæg, ingen pres om at sælge nyt udstyr. Jeg kommer for at fikse det, der er i stykker – og jeg kører først igen, når maskinen kører 100 % som den skal.
                </p>

                <p>
                  Jeg har selv to børn derhjemme, så jeg ved præcis, hvor frustrerende det er, når vaskemaskinen strejker midt i en travl hverdag, eller køleskabet pludselig ikke køler. Tingene i hjemmet skal bare fungere – uden drama, uden ventetid og uden at det koster en formue.
                </p>
              </div>

              {/* Hvorfor vælge mig sektion fra forside/scrape */}
              <div className="mt-8 pt-8 space-y-4">
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                  Hvorfor vælge mig
                </h3>

                <p className="text-sm text-slate-600 leading-relaxed">
                  Fordi jeg er en erfaren, fagligt stolt og klima-bevidst tekniker, der tager ejerskab for både kunden og maskinen.
                </p>

                <p className="text-sm text-slate-600 leading-relaxed">
                  Jeg har 10 års erfaring med service og reparation af hårde hvidevarer – vaskemaskiner, tørretumblere, opvaskemaskiner, køleskabe, frysere og ovne. Jeg kender de fleste mærker (Bosch, Siemens, Miele, Electrolux, Whirlpool, Samsung osv.) godt både mekanisk, elektronisk og med kølekomponenter.
                </p>

                <p className="text-sm text-slate-600 leading-relaxed">
                  Jeg er fagligt stolt. Det betyder, at jeg ikke laver halv-løsninger. Jeg finder fejlen, reparerer ordentligt første gang og efterlader altid maskinen ren og pæn. Det giver færre tilbagekald og meget tilfredse kunder.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default About;
